import React, { useState } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface RevenueParams {
  mining_power: number;
  selected_months: string;
  response_sample_rate: number;
  start_date: string;
}

interface FiatRevenue {
  date: number;  // Unix timestamp in seconds
  fiat_revenue: number;
}

interface SatoshiRevenue {
  date: number;  // Unix timestamp in seconds
  satoshi_revenue: number;  // using number instead of u64 as JavaScript doesn't have u64
}

interface RevenueResult {
  fiat_revenues: FiatRevenue[];
  satoshi_revenues: SatoshiRevenue[];
  hodlers_revenues: FiatRevenue[];
  total_fiat_revenue: number;
  total_satoshis: number;
  current_total_sats_fiat_value: number;
  current_exchange_rate: number;
}

interface ChartDataPoint {
  date: string;
  fiat_revenue: number;
  satoshi_revenue: number;
}

async function fetchCalculateRevenue(params: RevenueParams) {
  try {
    const response = await axios.put(API_ENDPOINTS.calculate_revenue, null, {
      params: {
        mining_power: params.mining_power,
        selected_months: params.selected_months,
        response_sample_rate: params.response_sample_rate,
        start_date: params.start_date
      }
    });
    console.log(response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching calculate revenue:', error);
    return null;
  }
}

function CalculateRevenue() {
  const [calculatorData, setCalculatorData] = useState<RevenueResult | null>(null);
  const [inputError, setInputError] = useState<string>('');
  const [params, setParams] = useState<RevenueParams>({
    mining_power: 110,
    selected_months: '1,2,3,4,10,11,12',
    response_sample_rate: 4,
    start_date: '2023-01-01T00:00:00Z'
  });

  const validateMonths = (months: string): boolean => {
    const monthArray = months
      .split(',')
      .map(m => parseInt(m))
      .filter(num => !isNaN(num));
    
    return monthArray.every(num => num >= 1 && num <= 12);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name === 'selected_months') {
      // Allow commas and numbers only
      const sanitizedValue = value.replace(/[^0-9,]/g, '');
      
      // Check if all months are valid
      const isValid = validateMonths(sanitizedValue);
      setInputError(isValid ? '' : 'Please enter valid months (1-12)');
      
      setParams(prev => ({ ...prev, selected_months: sanitizedValue }));
      return;
    }
    
    setParams(prev => ({
      ...prev,
      [name]: name === 'mining_power'
        ? Number(value)
        : name === 'start_date'
          ? `${value}T00:00:00Z`
          : value
    }));
  };

  const formatChartData = (data: RevenueResult): ChartDataPoint[] => {
    return data.fiat_revenues.map((fiat, index) => ({
      date: new Date(fiat.date * 1000).toLocaleDateString(),
      fiat_revenue: fiat.fiat_revenue / 1000,
      satoshi_revenue: data.satoshi_revenues[index].satoshi_revenue / 100000000,
      hodlers_revenue: data.hodlers_revenues[index].fiat_revenue / 1000,
    }));
  };

  const handleFetchData = async () => {
    // Clean up selected months before submitting
    const validMonths = params.selected_months
      .split(',')
      .map(m => parseInt(m))
      .filter(num => !isNaN(num) && num >= 1 && num <= 12)
      .join(',');

    const cleanedParams = {
      ...params,
      selected_months: validMonths
    };

    const data = await fetchCalculateRevenue(cleanedParams);
    setCalculatorData(data);
  };

  return (
    <div className="calculate-revenue" style={{ width: 'calc(100% - 2rem)', margin: '0 1rem' }}>
      <h1 style={{ textAlign: 'center' }}>Calculate Revenue</h1>
      
      <div className="input-form" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', marginBottom: '10px', alignItems: 'center' }}>
          <label htmlFor="mining_power" style={{ width: '300px', fontSize: '20px' }}
                 title="Mining Power (TH/s).  Defaults to that of a S19 Pro">Mining Power (TH/s): </label>
          <input
            type="number"
            id="mining_power"
            name="mining_power"
            value={params.mining_power}
            onChange={handleInputChange}
            step="1"
            min="0"
            style={{ width: '150px' }}
          />
        </div>
        
        <div style={{ display: 'flex', marginBottom: '10px', alignItems: 'center' }}>
          <label htmlFor="selected_months" style={{ width: '300px', fontSize: '20px' }}
                 title="Comma-separated list of months (1-12). Example: 1,2,3,12">Selected Months: </label>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <input
              type="text"
              id="selected_months"
              name="selected_months"
              value={params.selected_months}
              onChange={handleInputChange}
              style={{
                width: '150px',
                borderColor: inputError ? '#ff4444' : undefined
              }}
              placeholder="1,2,3,12"
            />
            {inputError && (
              <span style={{ color: '#ff4444', fontSize: '12px', marginTop: '4px' }}>
                {inputError}
              </span>
            )}
          </div>
        </div>
        
        <div style={{ display: 'flex', marginBottom: '10px', alignItems: 'center' }}>
        <label htmlFor="start_date" style={{ width: '300px', fontSize: '20px' }}
               title="Start date that mining would have started.  Defaults to January 1st, 2023.">Start Date: </label>
          <input
            type="date"
            id="start_date"
            name="start_date"
            value={params.start_date.split('T')[0]}
            onChange={handleInputChange}
            style={{ width: '150px' }}
          />
        </div>
      </div>

      <button 
        onClick={handleFetchData} 
        disabled={!!inputError}
        style={{
          marginBottom: '20px',
          padding: '10px 20px',
          backgroundColor: inputError ? '#cccccc' : '#4CAF50',
          color: 'white',
          border: 'none',
          borderRadius: '5px',
          fontSize: '16px',
          cursor: inputError ? 'not-allowed' : 'pointer',
          transition: 'background-color 0.3s ease'
        }}
        onMouseOver={(e) => !inputError && (e.currentTarget.style.backgroundColor = '#45a049')}
        onMouseOut={(e) => !inputError && (e.currentTarget.style.backgroundColor = '#4CAF50')}
      >
        Calculate Revenue
      </button>
      <div id="calculate-revenue-data">
        {calculatorData && (
          <div>
            <h2>Results</h2>
            <p 
              style={{ color: '#8884d8' }} 
              title="Revenue generated if sats earned from mining was exchanged for fiat"
            >
              Total Fiat Revenue: ${Math.round(calculatorData.total_fiat_revenue).toLocaleString('en-US')}
            </p>
            <p style={{ color: '#FFA500' }}
              title="Revenue generated if sats earned from mining was held to current date and not exchanged for fiat"
            >
              Hodler's Revenue: ${Math.round(calculatorData.current_total_sats_fiat_value).toLocaleString('en-US')} (at today's exchange rate of ${calculatorData.current_exchange_rate})
            </p>
            <p style={{ color: '#82ca9d' }}>Total Bitcoin Revenue (Sats): {Math.round(calculatorData.total_satoshis).toLocaleString()}</p>
            <div style={{ width: '100%', height: 500, marginTop: 40 }} className="recharts-cartesian-grid-outer">
              <ResponsiveContainer width="90%" height={500}>
                <LineChart
                  data={formatChartData(calculatorData)}
                  margin={{ top: 5, right: 90, left: 80, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date"
                    angle={-45}
                    textAnchor="end"
                    height={70}
                    interval={10}
                  />
                  <YAxis 
                    yAxisId="left"
                    label={{ value: 'Fiat Revenue (K$)', angle: -90, position: 'insideLeft', offset: -40, dy: 110, style: { fill: '#8884d8' } }}
                  />
                  <YAxis 
                    yAxisId="right" 
                    orientation="right"
                    label={{ value: 'Bitcoin Revenue (BTC)', angle: 90, position: 'insideRight', offset: -80, dy: 110, style: { fill: '#82ca9d' } }}
                  />
                  <Tooltip />
                  <Legend 
                    verticalAlign="bottom" 
                    layout="horizontal"
                    wrapperStyle={{ paddingTop: "60px" }}
                  />
                  <Line
                    yAxisId="left"
                    dataKey="fiat_revenue"
                    stroke="#8884d8"
                    name="Fiat Revenue (K$)"
                    dot={false}
                    strokeWidth={2}
                  />
                  <Line
                    yAxisId="left"
                    dataKey="hodlers_revenue"
                    stroke="#FFA500"
                    name="Hodlers Revenue (K$)"
                    dot={false}
                    strokeWidth={2}
                  />
                  <Line
                    yAxisId="right"
                    dataKey="satoshi_revenue"
                    stroke="#82ca9d"
                    name="Bitcoin Revenue (BTC)"
                    dot={false}
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CalculateRevenue; 
